import type { PoliticaDeSenha } from '@/auth/politicaDeSenha'
import type { ModoDeAuth, Sessao } from '@/auth/session'

/**
 * What `entrar()` can produce.
 *
 * Correct credentials do not always mean a session. On an invitation-only pool
 * (BR-08) Cognito puts every freshly invited user in `FORCE_CHANGE_PASSWORD`, so the
 * FIRST login of EVERY user answers a challenge instead of returning a session.
 *
 * Modelled as a returned union rather than a thrown error, because it is a normal
 * outcome of the happy path — the previous shape (throwing `ProximoPasso:…`) forced
 * the screen to read a success out of a `catch`.
 */
export type ResultadoDeEntrada = { tipo: 'sessao'; sessao: Sessao } | { tipo: 'novaSenhaRequerida' }

/**
 * The port every auth implementation fills.
 *
 * Story 0.3 AC-8: switching from the mocked session to the real Cognito pool must
 * touch no screen. This interface is where that promise is kept — screens see
 * `useSession()`, `useSession()` sees an `AdaptadorDeAuth`, and swapping the
 * implementation is a one-line change in `escolherAdaptador`.
 */
export interface AdaptadorDeAuth {
  readonly modo: ModoDeAuth

  /**
   * Restores a session on page load, or `null` when there is none. Must not throw
   * for the plain "not signed in" case — that is not an error.
   */
  restaurar(): Promise<Sessao | null>

  /** Throws `ErroDeAutenticacao` on any failure. */
  entrar(email: string, senha: string): Promise<ResultadoDeEntrada>

  /**
   * Answers the `novaSenhaRequerida` challenge opened by `entrar()` and completes
   * the sign-in.
   *
   * ⚠️ Stateful and short-lived: it is only valid immediately after the `entrar()`
   * that opened the challenge, because Amplify holds the challenge session in
   * memory. A reload loses it and the user signs in again — which is why the new
   * password step is a STATE of `/login` and not a route someone can bookmark.
   */
  definirNovaSenha(novaSenha: string): Promise<Sessao>

  /**
   * Starts self-service reset: asks the pool to e-mail a confirmation code.
   *
   * ⚠️ **Must resolve for an unknown e-mail too.** An invitation-only platform (BR-08)
   * that answers "no such account" hands out the list of who has access. Every
   * implementation swallows the not-found case and reports nothing either way — the
   * screen shows the same thing for both.
   */
  solicitarCodigoDeRedefinicao(email: string): Promise<void>

  /**
   * Finishes the reset with the e-mailed code and a new password.
   *
   * Unlike `definirNovaSenha`, this is **stateless and does NOT sign the user in** —
   * that is Cognito's own behaviour for `confirmResetPassword`, not a choice. The
   * caller sends the user back to the credentials form afterwards. Because the e-mail
   * is passed in rather than held between calls, a reload only costs the form state:
   * the code is still sitting in the user's inbox.
   */
  confirmarRedefinicaoDeSenha(email: string, codigo: string, novaSenha: string): Promise<void>

  /** The pool's policy, so the screen can list requirements before the round-trip. */
  politicaDeSenha(): Promise<PoliticaDeSenha>

  sair(): Promise<void>

  /**
   * A valid, non-expired token, refreshing it if needed (AC-2). `null` when there is
   * no session. Throws when a refresh fails — the bridge reads that as expiry.
   */
  obterToken(): Promise<string | null>
}

/**
 * An auth failure carrying the provider's error *name* — never its message.
 *
 * AC-9: AWS wording must not reach the screen. Keeping the name and dropping the
 * message means the UI translates a known code and falls back to one generic
 * sentence, instead of rendering "Incorrect username or password." in English.
 */
export class ErroDeAutenticacao extends Error {
  readonly codigo: string

  constructor(codigo: string) {
    super(codigo)
    this.name = 'ErroDeAutenticacao'
    this.codigo = codigo
  }
}
